import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, AlertCircle, CheckCircle, X } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { authApi } from '../../services/api';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState('');
    const [forgotSuccess, setForgotSuccess] = useState(false);
    const [forgotCaptchaToken, setForgotCaptchaToken] = useState(null);
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const recaptchaRef = useRef();
    const forgotRecaptchaRef = useRef();

    // Redirect to dashboard if already logged in (e.g. via remembered session)
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/admin/dashboard', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        const rememberedEmail = localStorage.getItem('adminRememberedEmail');
        if (rememberedEmail) {
            setEmail(rememberedEmail);
            setRememberMe(true);
        }
    }, []);

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Verify CAPTCHA
        if (!captchaToken) {
            setError('Lütfen CAPTCHA doğrulamasını tamamlayın.');
            return;
        }

        setLoading(true);

        const result = await login(email, password, captchaToken, rememberMe);

        if (result.success) {
            if (rememberMe) {
                localStorage.setItem('adminRememberedEmail', email);
            } else {
                localStorage.removeItem('adminRememberedEmail');
            }
            navigate('/admin/dashboard');
        } else {
            setError(result.error || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
            // Reset CAPTCHA on failed login
            recaptchaRef.current?.reset();
            setCaptchaToken(null);
        }

        setLoading(false);
    };

    const openForgotPassword = () => {
        setForgotEmail(email);
        setForgotError('');
        setForgotSuccess(false);
        setShowForgotPassword(true);
    };

    const closeForgotPassword = () => {
        setShowForgotPassword(false);
        setForgotEmail('');
        setForgotError('');
        setForgotSuccess(false);
        setForgotCaptchaToken(null);
        forgotRecaptchaRef.current?.reset();
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotError('');

        if (!forgotCaptchaToken) {
            setForgotError('Lütfen CAPTCHA doğrulamasını tamamlayın.');
            return;
        }

        setForgotLoading(true);
        try {
            await authApi.forgotPassword(forgotEmail, forgotCaptchaToken);
            setForgotSuccess(true);
        } catch (err) {
            setForgotError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
            forgotRecaptchaRef.current?.reset();
            setForgotCaptchaToken(null);
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-center">
                        <div className="flex justify-center mb-4">
                            <img src="/bobo-logo.png" alt="BOBO Yazılım" className="h-20 w-auto object-contain" />
                        </div>
                        <h1 className="text-2xl font-bold text-white">Temizlik Geri Bildirim Sistemi</h1>
                        <p className="text-blue-100 mt-2">Yönetim Paneli</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-8 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                E-posta Adresi
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="E-posta Adresi"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Şifre
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="rememberMe"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600 cursor-pointer select-none">
                                    Beni Hatırla <span className="text-gray-400">(30 Gün)</span>
                                </label>
                            </div>
                            <button
                                type="button"
                                onClick={openForgotPassword}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            >
                                Şifremi Unuttum
                            </button>
                        </div>

                        <div className="flex justify-center">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                onChange={handleCaptchaChange}
                                theme="light"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:ring-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Giriş yapılıyor...
                                </span>
                            ) : (
                                'Giriş Yap'
                            )}
                        </button>
                    </form>
                </div>
            </div>
            <footer className="text-center py-4 text-xs text-blue-300">
                © 2026 BOBO Yazılım · Tüm Hakları Saklıdır.
            </footer>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-white">Şifremi Unuttum</h2>
                            <button
                                onClick={closeForgotPassword}
                                className="text-blue-200 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            {forgotSuccess ? (
                                <div className="text-center space-y-4">
                                    <div className="flex justify-center">
                                        <CheckCircle className="w-16 h-16 text-green-500" />
                                    </div>
                                    <p className="text-gray-700 font-medium">Yeni şifreniz telefon numaranıza gönderildi.</p>
                                    <button
                                        onClick={closeForgotPassword}
                                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all"
                                    >
                                        Tamam
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleForgotPassword} className="space-y-4">
                                    <p className="text-sm text-gray-600">
                                        Kayıtlı e-posta adresinizi girin. Yeni şifreniz kayıtlı telefon numaranıza SMS ile gönderilecektir.
                                    </p>

                                    {forgotError && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                            <p className="text-sm text-red-700">{forgotError}</p>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            E-posta Adresi
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <input
                                                type="email"
                                                value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                placeholder="E-posta adresiniz"
                                                required
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-center">
                                        <ReCAPTCHA
                                            ref={forgotRecaptchaRef}
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={(token) => setForgotCaptchaToken(token)}
                                            onExpired={() => setForgotCaptchaToken(null)}
                                            theme="light"
                                        />
                                    </div>

                                    <div className="flex gap-3 pt-1">
                                        <button
                                            type="button"
                                            onClick={closeForgotPassword}
                                            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            İptal
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={forgotLoading}
                                            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {forgotLoading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    Gönderiliyor...
                                                </span>
                                            ) : (
                                                'Gönder'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginPage;
